import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { useHistory, useParams } from "react-router-dom";
import styled from "styled-components";
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';

import { Layout } from '~/components/UI';
import Drawer from "./Drawer";
import SummaryDrawer from "./Summary/SummaryDrawer";
import * as actions from "./actions";
import { Header } from "./Header";
import { LeadColumns } from "./LeadColumns";
import { LeadsTable } from "./LeadsTable";
import Summary from "./Summary";
import CadenceSelection from "./CadenceSelection";
import { useLeadsFilter } from './hooks/useLeadsFilter';
import { useDisplayMode } from './hooks/useDisplayMode';
import usePersistedFilters from '~/_shared/hooks/usePersistedFilters';
import RestoredFiltersNotice from '~/_shared/components/RestoredFiltersNotice';
import {
  SOCIAL_SELLING_FILTERS_KEY,
  getDefaultSocialSellingFilters,
  getSocialSellingLeadsRequest,
  getSocialSellingRestoredSummary,
} from './filters';

moment.updateLocale("pt-br", {
  weekdaysMin: ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"],
});

// Synchronized scroll container
const SynchronizedScrollContainer = styled.div`
  overflow-x: auto;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
  
  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari and Opera */
  }
`;

const FunnelContent = styled.div`
  display: flex;
  flex-direction: column;
  min-width: max-content;
  padding-top: 20px;
`;

const NavigationButton = styled.button`
  position: absolute;
  top: 0px;
  z-index: 1000;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  background: ${props => props.disabled ? '#d9d9d9' : '#1890ff'};
  border: none;
  color: white;
  font-size: 10px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.disabled ? '#d9d9d9' : '#40a9ff'};
    transform: ${props => props.disabled ? 'none' : 'scale(1.1)'};
  }
  
  &:active {
    transform: ${props => props.disabled ? 'none' : 'scale(0.95)'};
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const LeftButton = styled(NavigationButton)`
  left: 5px;
`;

const RightButton = styled(NavigationButton)`
  right: 5px;
`;

const Leads = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const params = useParams();
  const scrollContainerRef = useRef(null);

  const columns = useSelector((state) => state.socialSelling.columns);
  const isLoading = useSelector((state) => state.socialSelling.isLoading);
  const user = useSelector((state) => state.user);
  const selectedMonth = useSelector((state) => state.socialSelling.selectedMonth);
  const allCadences = useSelector(state => state.settings['socialSelling.cadence']);
  const summaryDrawer = useSelector((state) => state.socialSelling.summaryDrawer);

  // Period and search survive a refresh; cadence and display mode stay in the URL.
  const persisted = usePersistedFilters(SOCIAL_SELLING_FILTERS_KEY, getDefaultSocialSellingFilters);
  const filters = persisted.filters;
  const setFilters = persisted.setFilters;
  const date = filters.date;
  const dateMode = filters.dateMode;
  const searchQuery = filters.search;

  const setDate = useCallback(function(newDate) {
    setFilters({ date: newDate });
  }, [setFilters]);

  const setDateMode = useCallback(function(newMode) {
    setFilters({ dateMode: newMode });
  }, [setFilters]);

  const setSearchQuery = useCallback(function(value) {
    setFilters({ search: value });
  }, [setFilters]);

  // Keep redux selectedMonth (used by Summary, the table and the client-side
  // filter) on the applied date: on mount, on every date change and on
  // "Limpar filtros". Also re-syncs when the IDB cache restore (AppContext,
  // async, lands after mount) brings back the selectedMonth of another visit.
  useEffect(function() {
    if (selectedMonth !== date) {
      dispatch(actions.setSelectedMonth(date));
    }
  }, [dispatch, date, selectedMonth]);

  const [selectedCadence, setSelectedCadence] = useState(null);
  const [error, setError] = useState(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const { displayMode, updateDisplayMode } = useDisplayMode();

  // Read cadence from URL path parameter on mount and when URL changes
  useEffect(function() {
    var cadenceIdFromUrl = params.cadenceId;
    if (cadenceIdFromUrl) {
      var parsedId = parseInt(cadenceIdFromUrl, 10);
      if (!isNaN(parsedId) && parsedId !== selectedCadence) {
        setSelectedCadence(parsedId);
      }
    } else if (selectedCadence !== null) {
      // URL has no cadence but state has one - clear it (user navigated back)
      setSelectedCadence(null);
    }
  }, [params.cadenceId]);

  // Update URL when cadence is selected
  var handleCadenceSelect = useCallback(function(cadenceId) {
    setSelectedCadence(cadenceId);
    if (cadenceId !== null) {
      history.push('/crm/social-selling/' + cadenceId);
    } else {
      history.push('/crm/social-selling');
    }
  }, [history]);
  


  // Memoize callbacks to prevent unnecessary re-renders
  const showDrawer = useCallback((data) => {
    try {
      dispatch(actions.show(data));
    } catch (err) {
      setError('Failed to show drawer');
      console.error(err);
    }
  }, [dispatch]);

  const loadMoreLeads = useCallback(({ columnId, currentCount, moreCount }) => {
    try {
      dispatch(actions.loadMoreLeads({ columnId, currentCount, moreCount }));
    } catch (err) {
      setError('Failed to load more leads');
      console.error(err);
    }
  }, [dispatch]);

  const moveLead = useCallback((...props) => {
    try {
      dispatch(actions.move(props));
    } catch (err) {
      setError('Failed to move lead');
      console.error(err);
    }
  }, [dispatch]);

  const showSummaryDrawer = useCallback(function(summaryData) {
    // Open drawer with loading state
    dispatch(actions.showSummaryDrawer(summaryData));

    // Determine card_type for the API call
    var cardType = null;
    if (summaryData.columnId === 1) {
      cardType = 'leads';
    } else if (summaryData.columnId === 5) {
      cardType = 'positivo';
    } else if (summaryData.columnId === 6) {
      cardType = 'negativo';
    } else if (summaryData.columnId && summaryData.columnId.toString().startsWith('step_')) {
      cardType = summaryData.columnId.toString();
    }

    // Fetch leads from backend
    if (cardType) {
      var cadenceName = selectedCadenceData ? selectedCadenceData.name : null;
      dispatch(actions.loadSummaryCardLeads(cardType, cadenceName, selectedMonth));
    }
  }, [dispatch, selectedCadenceData, selectedMonth]);

  const closeSummaryDrawer = useCallback(() => {
    dispatch(actions.closeSummaryDrawer());
  }, [dispatch]);

  // Navigation scroll functions
  const scrollLeft = useCallback(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -300, behavior: 'smooth' });
    }
  }, []);

  const scrollRight = useCallback(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 300, behavior: 'smooth' });
    }
  }, []);

  // Check scroll position to enable/disable buttons
  const checkScrollButtons = useCallback(() => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
    }
  }, []);

  // Add scroll event listener (only once on mount)
  useEffect(function() {
    var scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', checkScrollButtons);
      checkScrollButtons(); // Check initial state

      return function() {
        scrollContainer.removeEventListener('scroll', checkScrollButtons);
      };
    }
  }, [checkScrollButtons]);

  // Re-check scroll buttons when columns change (content width may have changed)
  useEffect(function() {
    checkScrollButtons();
  }, [columns, checkScrollButtons]);

  // Memoize the header props to prevent unnecessary re-renders
  const headerProps = useMemo(() => ({
    date,
    setDate,
    dateMode,
    setDateMode,
    defaultFilters: persisted.defaults,
    onClearFilters: persisted.resetFilters,
    displayMode,
    updateDisplayMode,
    user,
    showDrawer,
    title: "Social Selling",
    searchQuery,
    setSearchQuery,
    selectedCadence,
    setSelectedCadence: handleCadenceSelect
  }), [date, setDate, dateMode, setDateMode, persisted.defaults, persisted.resetFilters, displayMode, updateDisplayMode, user, showDrawer, searchQuery, setSearchQuery, selectedCadence, handleCadenceSelect]);

  useEffect(() => {
    const loadData = async () => {
      try {
        // Only load full lead data when a cadence is selected
        // Landing page uses optimized cadenceSummary endpoint
        if (selectedCadence !== null) {
          await dispatch(actions.loadLeads(getSocialSellingLeadsRequest({ date: date })));
        }
      } catch (err) {
        setError('Failed to load leads');
        console.error(err);
      }
    };
    loadData();
  }, [dispatch, date, selectedCadence]);

  // No longer auto-select first cadence - let user choose from landing page

  // Get selected cadence data
  const selectedCadenceData = useMemo(() => {
    if (!allCadences) {
      return null;
    }
    
    // If no cadence is selected, use the first one (default)
    const cadenceId = selectedCadence !== null ? selectedCadence : (allCadences[0] && allCadences[0].id || null);
    
    if (!cadenceId) {
      return null;
    }
    
    const cadence = allCadences.find(c => c.id === cadenceId);
    
    if (!cadence) {
      return null;
    }
    
    try {
      const parsedValue = JSON.parse(cadence.value);
      return {
        name: parsedValue.name,
        data: parsedValue.data
      };
    } catch (e) {
      console.error('Error parsing cadence:', e);
      return null;
    }
  }, [allCadences, selectedCadence]);

  // Use the filtered data
  const { filteredData, filteredDataToColumns } = useLeadsFilter(columns, searchQuery, selectedCadenceData, selectedMonth);

  // Update columns when cadence changes
  useEffect(() => {
    if (selectedCadenceData) {
      dispatch(actions.updateColumnsForCadence(selectedCadenceData));
    }
  }, [selectedCadenceData, dispatch]);

  if (error) {
    return (
      <Layout>
        <div className="error-message">
          {error}
          <button onClick={() => setError(null)}>Dismiss</button>
        </div>
      </Layout>
    );
  }

  // Show landing page if no cadence selected
  if (selectedCadence === null) {
    return (
      <Layout>
        <Header {...headerProps} />
        <CadenceSelection onSelect={handleCadenceSelect} />
      </Layout>
    );
  }

  return (
    <Layout>
      <Header {...headerProps} />
      <RestoredFiltersNotice
        visible={persisted.wasRestored}
        summary={getSocialSellingRestoredSummary(filters, persisted.defaults)}
        onReset={persisted.resetFilters}
        onDismiss={persisted.dismissRestored}
      />
      {displayMode === "funnel" && (
        <div style={{ position: 'relative' }}>
          <LeftButton onClick={scrollLeft} disabled={!canScrollLeft}>
            <FaChevronLeft />
          </LeftButton>
          <RightButton onClick={scrollRight} disabled={!canScrollRight}>
            <FaChevronRight />
          </RightButton>
          <SynchronizedScrollContainer ref={scrollContainerRef}>
            <FunnelContent>
              <Summary
                selectedCadence={selectedCadence}
                selectedCadenceData={selectedCadenceData}
                onSummaryCardClick={showSummaryDrawer}
              />
              <LeadColumns
                columns={filteredDataToColumns}
                moveLead={moveLead}
                date={date}
                isLoading={isLoading}
                showDrawer={showDrawer}
                loadMoreLeads={loadMoreLeads}
                user={user}
              />
            </FunnelContent>
          </SynchronizedScrollContainer>
        </div>
      )}
      {displayMode === "table" && (
        <LeadsTable
          showDrawer={showDrawer}
          data={filteredData}
          isLoading={isLoading}
          columns={columns}
        />
      )}
      <Drawer selectedCadence={selectedCadence} />
      <SummaryDrawer
        isVisible={(summaryDrawer && summaryDrawer.show) || false}
        onClose={closeSummaryDrawer}
        summaryData={(summaryDrawer && summaryDrawer.summaryData) || null}
        leads={(summaryDrawer && summaryDrawer.leads) || []}
        isLoading={(summaryDrawer && summaryDrawer.isLoading) || false}
        allColumns={columns}
      />
    </Layout>
  );
};

export default React.memo(Leads);
